import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { IndianRupee, Save, FileSpreadsheet, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormulaInput } from "@/components/FormulaInput";
import { saveOrderPricing, getOrderPricingSuggestion, formatApiError } from "@/lib/api";
import { unitOf, evalFormula, formatDate, formatDateTime } from "@/lib/fabricData";

const money = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const effQty = (it) => (it.delivered_kg !== null && it.delivered_kg !== undefined ? it.delivered_kg : it.quantity_kg);

export function OrderPricing({ token, order, onSaved }) {
  const items = order.items || [];
  const [rateDrafts, setRateDrafts] = useState({});
  const [freight, setFreight] = useState("");
  const [gst, setGst] = useState("");
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);

  // Init only when the section is opened — avoids firing a suggestion request
  // for every order card in the list (which caused the Orders panel to freeze).
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    const init = async () => {
      if (order.pricing_saved) {
        const rd = {};
        items.forEach((it, i) => { if (it.rate !== null && it.rate !== undefined) rd[i] = String(it.rate); });
        setRateDrafts(rd);
        setFreight(order.freight === null || order.freight === undefined ? "" : String(order.freight));
        setGst(order.gst_percent === null || order.gst_percent === undefined ? "" : String(order.gst_percent));
        setReady(true);
        return;
      }
      try {
        const sug = await getOrderPricingSuggestion(token, order.id);
        if (cancel) return;
        const rd = {};
        (sug.items || []).forEach((s, i) => { if (s.rate !== null && s.rate !== undefined) rd[i] = String(s.rate); });
        setRateDrafts(rd);
        setFreight(sug.freight === null || sug.freight === undefined ? "" : String(sug.freight));
        setGst(sug.gst_percent === null || sug.gst_percent === undefined ? "" : String(sug.gst_percent));
      } catch { /* leave blank */ }
      if (!cancel) setReady(true);
    };
    init();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order.id, order.pricing_saved]);

  const numRate = (i) => { const v = evalFormula(rateDrafts[i]); if (v !== null) return v; const r = items[i]?.rate; return r === null || r === undefined ? 0 : Number(r); };
  const numFreight = useMemo(() => { const v = evalFormula(freight); if (v !== null) return v; return Number(order.freight || 0); }, [freight, order.freight]);
  const numGst = useMemo(() => { const v = evalFormula(gst); if (v !== null) return v; return Number(order.gst_percent || 0); }, [gst, order.gst_percent]);

  const lineSub = (it, i) => numRate(i) * effQty(it);
  const subtotal = items.reduce((a, it, i) => a + lineSub(it, i), 0);
  const gstAmt = subtotal * (numGst / 100);
  const afterGst = subtotal + gstAmt;
  const grand = afterGst + numFreight;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        items: items.map((_, i) => ({ index: i, rate: rateDrafts[i] === undefined || String(rateDrafts[i]).trim() === "" ? null : (evalFormula(rateDrafts[i]) ?? 0) })),
        freight: numFreight,
        gst_percent: numGst,
      };
      const updated = await saveOrderPricing(token, order.id, payload);
      toast.success("Pricing saved & locked to this order");
      onSaved && onSaved(updated);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const alteredLabel = (by) => (by === "admin" ? "admin altered" : by === "coadmin" ? "co-ad altered" : "");
  const downloadExcel = () => {
    const rows = [];
    const custLabel = order.client_altered_by ? ` (${alteredLabel(order.client_altered_by)})` : "";
    rows.push(["Customer", `${order.client_name || ""}${custLabel}`]);
    rows.push(["Company", order.company_name || ""]);
    rows.push(["Phone", order.phone || ""]);
    rows.push(["Order Date", order.created_at ? formatDateTime(order.created_at) : ""]);
    rows.push(["Status", order.status || "Pending"]);
    if (order.message && String(order.message).trim()) {
      rows.push(["Customer Message", String(order.message).trim()]);
    }
    rows.push([]);
    rows.push(["Fabric", "Colour", "Altered", "Qty Ordered", "Qty Delivered", "Unit", "Rate (₹)", "Line Subtotal (₹)"]);
    items.forEach((it, i) => {
      if (it.deleted_by) return;
      rows.push([
        it.fabric, it.color, alteredLabel(it.altered_by), it.quantity_kg,
        it.delivered_kg === null || it.delivered_kg === undefined ? "" : it.delivered_kg,
        unitOf(it.fabric), numRate(i), Number(lineSub(it, i).toFixed(2)),
      ]);
    });
    rows.push([]);
    rows.push(["Subtotal (₹)", Number(subtotal.toFixed(2))]);
    rows.push([`GST % `, numGst]);
    rows.push(["GST Amount (₹)", Number(gstAmt.toFixed(2))]);
    rows.push(["Freight (₹)", Number(numFreight.toFixed(2))]);
    rows.push(["Grand Total (₹)", Number(grand.toFixed(2))]);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Order");
    const safe = (order.client_name || "order").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    XLSX.writeFile(wb, `order-${safe}-${formatDate(order.created_at) || new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="px-5 py-3 border-t border-white/10 bg-[#0B0B0B]" data-testid={`order-pricing-${order.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => setOpen((o) => !o)} data-testid={`order-pricing-toggle-${order.id}`} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/50 hover:text-white transition-colors">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Pricing (admin only){order.pricing_saved ? <span className="ml-1 text-emerald-400/80 normal-case tracking-normal">· ₹{money(grand)} locked</span> : null}
        </button>
        <Button onClick={downloadExcel} variant="outline" data-testid={`order-excel-${order.id}`} className="rounded-none h-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
          <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Download Excel
        </Button>
      </div>
      {open && (<>
      <div className="space-y-1.5 mt-3">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-3 text-sm" data-testid={`order-pricing-item-${order.id}-${i}`}>
            <span className="flex-1 min-w-0 truncate text-white/80">{it.fabric} · {it.color}</span>
            <span className="tabular text-white/50 text-xs w-24 text-right">{effQty(it)} {unitOf(it.fabric)}</span>
            <div className="relative">
              <IndianRupee className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
              <FormulaInput value={rateDrafts[i] ?? ""} onValueChange={(val) => setRateDrafts((p) => ({ ...p, [i]: val }))}
                placeholder="rate" data-testid={`order-rate-${order.id}-${i}`} className="rounded-none h-8 w-28 pl-7 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
            </div>
            <span className="tabular text-white w-28 text-right" data-testid={`order-linesub-${order.id}-${i}`}>₹{money(lineSub(it, i))}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-white/10">
        <div className="flex items-center gap-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">GST %</Label>
          <FormulaInput value={gst} onValueChange={setGst} placeholder="0" data-testid={`order-gst-${order.id}`} className="rounded-none h-8 w-20 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Freight ₹</Label>
          <FormulaInput value={freight} onValueChange={setFreight} placeholder="0" data-testid={`order-freight-${order.id}`} className="rounded-none h-8 w-24 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
        </div>
        <div className="flex-1" />
        <Button onClick={save} disabled={saving || !ready} data-testid={`order-pricing-save-${order.id}`} className="rounded-none h-8 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider disabled:opacity-40">
          <Save className="h-3.5 w-3.5 mr-1.5" /> Save Pricing
        </Button>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <div className="border border-white/10 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-white/40">Subtotal</p><p className="tabular text-white" data-testid={`order-subtotal-${order.id}`}>₹{money(subtotal)}</p></div>
        <div className="border border-white/10 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-white/40">GST ({numGst}%)</p><p className="tabular text-white" data-testid={`order-gstamt-${order.id}`}>₹{money(gstAmt)}</p></div>
        <div className="border border-white/10 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-white/40">After GST + Freight</p><p className="tabular text-white/70">₹{money(afterGst)} + ₹{money(numFreight)}</p></div>
        <div className="border border-emerald-500/40 bg-emerald-500/10 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-emerald-300/70">Grand Total</p><p className="tabular text-emerald-300 font-bold" data-testid={`order-grandtotal-${order.id}`}>₹{money(grand)}</p></div>
      </div>
      </>)}
    </div>
  );
}
